<template>
  <!-- Toggle button — only when panel is closed -->
  <button
    v-if="!open"
    class="info-toggle"
    @click="open = true"
    title="Open info panel"
  >
    <span class="info-toggle__icon">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="16" x2="12" y2="12"/>
        <line x1="12" y1="8" x2="12.01" y2="8"/>
      </svg>
    </span>
    <span class="info-toggle__label">Info</span>
  </button>

  <!-- Slide-in drawer -->
  <transition name="info-drawer">
    <div v-if="open" class="info-drawer">
      <div class="drawer-header">
        <span class="drawer-title">Aktív rétegek</span>
        <button class="collapse-btn" title="Bezárás" @click="open = false">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
      </div>

      <div class="drawer-body">
        <!-- Active layers section -->
        <div v-if="activeDescriptors.length === 0" class="empty-msg">
          Nincs aktív réteg.
        </div>
        <div
          v-for="(d, i) in activeDescriptors"
          :key="i"
          class="layer-entry"
        >
          <span class="layer-group">{{ d.groupTitle }}</span>
          <span class="layer-node">{{ d.nodeTitle }}</span>
          <span v-if="d.detail" class="layer-detail">{{ d.detail }}</span>
        </div>

        <!-- Swipe layer section -->
        <template v-if="swipeTarget">
          <div class="section-divider" />
          <div class="swipe-section">
            <span class="swipe-label">Összehasonlító réteg</span>
            <span class="swipe-name">{{ swipeLayerName }}</span>
          </div>
        </template>

        <!-- Placeholder for future map tools -->
        <!-- TODO: add map tool controls here -->
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useFarms } from '@/composables/useFarms'
import { useLayerSelection } from '@/composables/useLayerSelection'
import { descendScope, type SelectionScope } from '@/composables/layerScope'
import type { LayerTreeNode, ActiveLayerDescriptor } from '@/types/layerTree'

const { layerTree } = useFarms()
const { activeNodesByGroup, selectedCogByNode, swipeTarget } = useLayerSelection()

const open = ref(false)

// Walk the tree and collect a descriptor for each active node.
function collectDescriptors(
  nodes: LayerTreeNode[],
  out: ActiveLayerDescriptor[],
  scope?: SelectionScope,
  parentGroupTitle?: string,
) {
  for (const node of nodes) {
    if (node.type === 'group') {
      // The heading still shows the immediate parent group, but activeness is
      // resolved against the selection scope (which may be an ancestor group).
      collectDescriptors(node.children, out, descendScope(scope, node), node.title)
    } else if (scope != null && activeNodesByGroup.value.get(scope.scopeId)?.has(node.id)) {
      const groupTitle = parentGroupTitle ?? ''
      if (node.type === 'raster_type') {
        const cogId = selectedCogByNode.value.get(node.id)
        const cog = cogId != null ? node.cogs.find(c => c.id === cogId) : undefined
        out.push({
          groupTitle,
          nodeTitle: node.title,
          detail: cog
            ? new Date(cog.sensing_date).toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' })
            : undefined,
        })
      } else if (node.type === 'raster_layer') {
        out.push({ groupTitle, nodeTitle: node.raster_layer.name })
      } else if (node.type === 'vector') {
        out.push({ groupTitle, nodeTitle: node.title })
      }
    }
  }
}

const activeDescriptors = computed<ActiveLayerDescriptor[]>(() => {
  const out: ActiveLayerDescriptor[] = []
  collectDescriptors(layerTree.value, out)
  return out
})

// Resolve the swipe layer display name from the tree.
const swipeLayerName = computed<string>(() => {
  if (!swipeTarget.value) return ''
  const key = swipeTarget.value._key
  if (key.startsWith('cog-')) {
    const cogId = parseInt(key.slice(4))
    return findCogName(layerTree.value, cogId) ?? key
  }
  if (key.startsWith('rl-')) {
    const nodeId = parseInt(key.slice(3))
    return findRasterLayerName(layerTree.value, nodeId) ?? key
  }
  return key
})

function findCogName(nodes: LayerTreeNode[], cogId: number): string | undefined {
  for (const node of nodes) {
    if (node.type === 'group') {
      const found = findCogName(node.children, cogId)
      if (found) return found
    } else if (node.type === 'raster_type') {
      const cog = node.cogs.find(c => c.id === cogId)
      if (cog) {
        const date = new Date(cog.sensing_date).toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' })
        return `${node.title} — ${date}`
      }
    }
  }
}

function findRasterLayerName(nodes: LayerTreeNode[], nodeId: number): string | undefined {
  for (const node of nodes) {
    if (node.type === 'group') {
      const found = findRasterLayerName(node.children, nodeId)
      if (found) return found
    } else if (node.type === 'raster_layer' && node.id === nodeId) {
      return node.raster_layer.name
    }
  }
}
</script>

<style scoped>
/* ── Toggle button ──────────────────────────────────────────────────────── */
.info-toggle {
  position: fixed;
  top: 72px;
  left: 46px;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px 8px 10px;
  background: #1e1e2e;
  border: 1px solid #313244;
  border-radius: 8px;
  color: #cdd6f4;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 2px 12px rgba(0,0,0,0.4);
  transition: background 0.15s, border-color 0.15s;
}
.info-toggle:hover {
  background: #313244;
  border-color: #89b4fa;
  color: #89b4fa;
}
.info-toggle__icon { display: flex; align-items: center; }

/* ── Drawer ─────────────────────────────────────────────────────────────── */
.info-drawer {
  position: fixed;
  top: 70px;
  left: 46px;
  z-index: 19;
  width: 260px;
  max-height: calc(100vh - 80px);
  background: #1e1e2e;
  border: 1px solid #313244;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.5);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 12px 8px 16px;
  flex-shrink: 0;
  border-bottom: 1px solid #313244;
}
.drawer-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #89b4fa;
}

/* ── Collapse button ────────────────────────────────────────────────────── */
.collapse-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  background: none;
  border: 1px solid #313244;
  border-radius: 6px;
  color: #6c7086;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s, background 0.15s;
}
.collapse-btn:hover { color: #89b4fa; border-color: #89b4fa; background: rgba(137,180,250,0.08); }

.drawer-body {
  padding: 10px 14px 14px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.empty-msg { font-size: 12px; color: #585b70; }

/* ── Active layer entries ───────────────────────────────────────────────── */
.layer-entry {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 5px 8px;
  background: rgba(137,180,250,0.06);
  border-radius: 6px;
  border-left: 2px solid #89b4fa;
}
.layer-group {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6c7086;
}
.layer-node {
  font-size: 13px;
  font-weight: 600;
  color: #cdd6f4;
}
.layer-detail {
  font-size: 11px;
  color: #a6adc8;
}

/* ── Swipe section ──────────────────────────────────────────────────────── */
.section-divider {
  height: 1px;
  background: #313244;
  margin: 2px 0;
}
.swipe-section {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 5px 8px;
  background: rgba(166,227,161,0.06);
  border-radius: 6px;
  border-left: 2px solid #a6e3a1;
}
.swipe-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6c7086;
}
.swipe-name {
  font-size: 12px;
  font-weight: 500;
  color: #a6e3a1;
}

/* ── Drawer slide transition ────────────────────────────────────────────── */
.info-drawer-enter-active,
.info-drawer-leave-active { transition: opacity 0.2s, transform 0.2s; }
.info-drawer-enter-from,
.info-drawer-leave-to { opacity: 0; transform: translateX(-16px); }
</style>
